import React, { useState, useEffect, useContext } from 'react';
import {
  Card, Button, Tag, Space, Modal, Form, Input, Select,
  message, Typography, Popconfirm, Alert, Spin, List, Table, Tooltip
} from 'antd';
import {
  KeyOutlined, PlusOutlined, DeleteOutlined, StopOutlined,
  QrcodeOutlined, CheckCircleOutlined, MobileOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { getTokens, createToken, deleteToken, revokeToken } from '../../api/services/tokenService';
import { getCurrentApiBaseUrl } from '../../api/config';
import { AuthContext } from '../../context/AuthContext';
import { Capacitor } from '@capacitor/core';

const { Text } = Typography;

const TokenManagement = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [reconnectQrModalVisible, setReconnectQrModalVisible] = useState(false);
  const [selectedTokenForQr, setSelectedTokenForQr] = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();
  const { currentUser } = useContext(AuthContext);
  const isMobile = Capacitor.isNativePlatform();

  // Tokens laden
  const loadTokens = async () => {
    setLoading(true);
    try {
      const data = await getTokens();
      setTokens(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Load tokens error:', error);
      // Fehler leise behandeln - keine Fehlermeldung anzeigen
      // um UI-Crash zu vermeiden
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Verzögert laden um Initialisierungsprobleme zu vermeiden
    const timer = setTimeout(() => {
      loadTokens();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Token erstellen
  const handleCreate = async (values) => {
    setCreating(true);
    try {
      const data = await createToken({
        name: values.name,
        expires_in_days: values.expires_in_days || null
      });
      setNewToken(data);
      setCreateModalVisible(false);
      setQrModalVisible(true);
      form.resetFields();
      loadTokens();
      message.success('Token erfolgreich erstellt');
    } catch (error) {
      message.error('Fehler beim Erstellen des Tokens');
      console.error('Create token error:', error);
    } finally {
      setCreating(false);
    }
  };

  // Token löschen
  const handleDelete = async (tokenId) => {
    try {
      await deleteToken(tokenId);
      message.success('Token gelöscht');
      loadTokens();
    } catch (error) {
      message.error('Fehler beim Löschen des Tokens');
    }
  };

  // Token widerrufen
  const handleRevoke = async (tokenId) => {
    try {
      await revokeToken(tokenId);
      message.success('Token widerrufen');
      loadTokens();
    } catch (error) {
      message.error('Fehler beim Widerrufen des Tokens');
    }
  };

  // QR-Code Daten als JSON-String für die App (mit Token - nur bei Erstellung)
  const getQrCodeValue = () => {
    if (!newToken?.qr_code_data) return '';
    return JSON.stringify(newToken.qr_code_data);
  };

  // QR-Code für Reconnect (ohne Token, nur API-URL und User-Info)
  const getReconnectQrCodeValue = () => {
    if (!selectedTokenForQr) return '';
    const apiUrl = getCurrentApiBaseUrl();
    return JSON.stringify({
      api_url: apiUrl,
      user_id: currentUser?.id,
      username: currentUser?.username,
      device_name: selectedTokenForQr.name,
      // Kein Token - App muss sich neu authentifizieren oder Token manuell eingeben
      requires_auth: true
    });
  };

  // QR-Code Modal für existierenden Token öffnen
  const showReconnectQrCode = (token) => {
    setSelectedTokenForQr(token);
    setReconnectQrModalVisible(true);
  };

  // Formatierung des Datums
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Token-Status Tag
  const getStatusTag = (record) => {
    if (!record.is_active) {
      return <Tag color="default" icon={<StopOutlined />}>Widerrufen</Tag>;
    }
    if (record.is_expired) {
      return <Tag color="red" icon={<ClockCircleOutlined />}>Abgelaufen</Tag>;
    }
    return <Tag color="green" icon={<CheckCircleOutlined />}>Aktiv</Tag>;
  };

  // Render Token-Aktionen
  const renderTokenActions = (record) => (
    <Space size="small">
      {record.is_active && !record.is_expired && (
        <>
          <Button
            type="text"
            size="small"
            icon={<QrcodeOutlined />}
            onClick={() => showReconnectQrCode(record)}
          />
          <Popconfirm
            title="Token widerrufen?"
            description="Der Token kann danach nicht mehr verwendet werden."
            onConfirm={() => handleRevoke(record.id)}
            okText="Widerrufen"
            cancelText="Abbrechen"
          >
            <Button
              type="text"
              size="small"
              icon={<StopOutlined />}
              danger
            />
          </Popconfirm>
        </>
      )}
      <Popconfirm
        title="Token löschen?"
        description="Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={() => handleDelete(record.id)}
        okText="Löschen"
        cancelText="Abbrechen"
      >
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          danger
        />
      </Popconfirm>
    </Space>
  );

  // Mobile-freundliche Token-Liste
  const renderTokenList = () => (
    <List
      dataSource={tokens}
      locale={{ emptyText: 'Keine Tokens vorhanden.' }}
      renderItem={(token) => (
        <List.Item
          key={token.id}
          actions={[renderTokenActions(token)]}
          style={{ padding: '12px 0' }}
        >
          <List.Item.Meta
            avatar={<MobileOutlined style={{ fontSize: '24px', color: '#1890ff' }} />}
            title={
              <Space>
                <Text strong>{token.name}</Text>
                {getStatusTag(token)}
              </Space>
            }
            description={
              <Space direction="vertical" size={0} style={{ fontSize: '12px' }}>
                <Text type="secondary">
                  Erstellt: {formatDate(token.created_at)}
                </Text>
                <Text type="secondary">
                  Gültig bis: {token.expires_at ? formatDate(token.expires_at) : 'Unbegrenzt'}
                </Text>
              </Space>
            }
          />
        </List.Item>
      )}
    />
  );

  // Desktop-Tabelle
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <MobileOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => getStatusTag(record),
    },
    {
      title: 'Erstellt am',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date),
    },
    {
      title: 'Gültig bis',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date) => date ? formatDate(date) : <Tag color="blue">Unbegrenzt</Tag>,
    },
    {
      title: 'Letzter Zugriff',
      key: 'last_used',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">{record.last_used_at ? formatDate(record.last_used_at) : 'Nie'}</Text>
          {record.last_used_ip && (
            <Text type="secondary" style={{ fontSize: '12px' }}>IP: {record.last_used_ip}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_, record) => renderTokenActions(record),
    },
  ];

  const renderDesktopTable = () => (
    <Table
      dataSource={tokens}
      columns={columns}
      rowKey="id"
      pagination={false}
      locale={{ emptyText: 'Keine Tokens vorhanden. Erstellen Sie einen neuen Token.' }}
    />
  );

  return (
    <Card
      title={
        <Space>
          <KeyOutlined />
          <span>API-Token Verwaltung</span>
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
        >
          Neuer Token
        </Button>
      }
      style={{ marginBottom: '24px' }}
    >
      <Alert
        message="Mobile App Authentifizierung"
        description="Erstellen Sie API-Tokens, um sich in der Android/iOS App anzumelden. Scannen Sie einfach den QR-Code mit der App."
        type="info"
        showIcon
        icon={<QrcodeOutlined />}
        style={{ marginBottom: '16px' }}
      />

      <Spin spinning={loading}>
        {isMobile ? renderTokenList() : renderDesktopTable()}
      </Spin>

      {/* Token erstellen Modal */}
      <Modal
        title={<Space><PlusOutlined /> Neuen API-Token erstellen</Space>}
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="name"
            label="Gerätename"
            rules={[{ required: true, message: 'Bitte Gerätenamen eingeben' }]}
          >
            <Input
              prefix={<MobileOutlined />}
              placeholder="z.B. Samsung Galaxy S21"
            />
          </Form.Item>

          <Form.Item
            name="expires_in_days"
            label="Gültigkeit"
          >
            <Select placeholder="Gültigkeit auswählen" allowClear>
              <Select.Option value={null}>Unbegrenzt</Select.Option>
              <Select.Option value={7}>7 Tage</Select.Option>
              <Select.Option value={30}>30 Tage</Select.Option>
              <Select.Option value={90}>90 Tage</Select.Option>
              <Select.Option value={365}>1 Jahr</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={creating}>
                Token erstellen
              </Button>
              <Button onClick={() => {
                setCreateModalVisible(false);
                form.resetFields();
              }}>
                Abbrechen
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* QR-Code Modal */}
      <Modal
        title={<Space><QrcodeOutlined /> QR-Code für Mobile App</Space>}
        open={qrModalVisible}
        onCancel={() => {
          setQrModalVisible(false);
          setNewToken(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setQrModalVisible(false);
            setNewToken(null);
          }}>
            Schließen
          </Button>
        ]}
        width={450}
      >
        {newToken && (
          <Space direction="vertical" style={{ width: '100%' }} align="center">
            <Alert
              message="Token nur einmal sichtbar!"
              description="Der Token wird nur jetzt angezeigt. Scannen Sie den QR-Code mit Ihrer App oder kopieren Sie den Token."
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            {/* QR-Code */}
            <div style={{
              padding: '20px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              textAlign: 'center'
            }}>
              <QRCodeSVG
                value={getQrCodeValue()}
                size={256}
                level="M"
                includeMargin={true}
              />
            </div>

            <Text type="secondary">Scannen Sie diesen QR-Code mit der Pool Billard App</Text>

            {/* Token anzeigen */}
            <div style={{ width: '100%', marginTop: '16px' }}>
              <Text strong>Token:</Text>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '8px',
                padding: '12px',
                background: '#f5f5f5',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                wordBreak: 'break-all'
              }}>
                <Text copyable={{ text: newToken.token }}>
                  {newToken.token}
                </Text>
              </div>
            </div>

            {/* Gerätename */}
            <div style={{ width: '100%', marginTop: '8px' }}>
              <Text type="secondary">Gerät: {newToken.name}</Text>
            </div>
          </Space>
        )}
      </Modal>

      {/* Reconnect QR-Code Modal (ohne Token) */}
      <Modal
        title={<Space><QrcodeOutlined /> QR-Code für Verbindung</Space>}
        open={reconnectQrModalVisible}
        onCancel={() => {
          setReconnectQrModalVisible(false);
          setSelectedTokenForQr(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setReconnectQrModalVisible(false);
            setSelectedTokenForQr(null);
          }}>
            Schließen
          </Button>
        ]}
        width={450}
      >
        {selectedTokenForQr && (
          <Space direction="vertical" style={{ width: '100%' }} align="center">
            <Alert
              message="Verbindungs-QR-Code (ohne Token)"
              description="Dieser QR-Code enthält nur die API-URL und Benutzerinformationen. Der Token muss separat in der App eingegeben werden oder ist bereits dort gespeichert."
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            {/* QR-Code */}
            <div style={{
              padding: '20px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              textAlign: 'center'
            }}>
              <QRCodeSVG
                value={getReconnectQrCodeValue()}
                size={256}
                level="M"
                includeMargin={true}
              />
            </div>

            <Text type="secondary">Scannen Sie diesen QR-Code mit der Pool Billard App</Text>

            {/* Infos anzeigen */}
            <div style={{ width: '100%', marginTop: '16px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>API-URL: </Text>
                  <Text copyable>{getCurrentApiBaseUrl()}</Text>
                </div>
                <div>
                  <Text strong>Benutzer: </Text>
                  <Text>{currentUser?.username}</Text>
                </div>
                <div>
                  <Text strong>Gerät: </Text>
                  <Text>{selectedTokenForQr.name}</Text>
                </div>
              </Space>
            </div>
          </Space>
        )}
      </Modal>
    </Card>
  );
};

export default TokenManagement;
